/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useEffect, useState } from 'react';

import { Grid } from '@coursera/cds-core';

import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { AutoSaveContext } from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
import NextButtonSubmitterFlow from 'bundles/grading/components/staff-graded-assignments/linear-nav/submitter-flow/NextButtonSubmitterFlow';
import PreviousButtonSubmitterFlow from 'bundles/grading/components/staff-graded-assignments/linear-nav/submitter-flow/PreviousButtonSubmitterFlow';
import type {
  LinearNavigationButtonInfo,
  NavigationShortcutCallbacks,
} from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { shouldAutoFocusNextButton } from 'bundles/grading/utils/autoFocusUtils';
import { useEventTracker } from 'bundles/grading/utils/hooks';

import _t from 'i18n!nls/grading';

const styles = {
  container: css`
    width: 100%;
    padding: var(--cds-spacing-300) var(--cds-spacing-200);
    display: flex;
    flex-direction: row-reverse;
  `,
  buttonColumn: css`
    display: flex;
    flex-direction: column;
  `,
  shortcuts: css`
    padding: var(--cds-spacing-150) 0 0 0;
    color: var(--cds-color-neutral-primary-weak);
    font-size: 15px;
  `,
  alignRight: css`
    align-self: end;
  `,
  shortcut: css`
    display: inline-block;
    padding: 0 var(--cds-spacing-100);
    background-color: var(--cds-color-grey-50);
    border-radius: 5px;
  `,
  shortcutCharacter: css`
    position: relative;
    bottom: var(--cds-spacing-50);
  `,
};

// Here we set a keydown event listener when the LinearNav mounts and remove it when the LinearNav un-mounts.
// The function that is invoked by the next and the previous button is recalculated for each rubric, that's why we use
// the NavigationShortcutCallbacks object to store the next/previous functions.
// We pass the object to the NextButtonSubmitterFlow and the PreviousButtonSubmitterFlow where we subsequently update
// the functions. Then the event listener calls the right functions stored in the NavigationShortcutCallbacks.
const useShortcuts = () => {
  const [shortcutCallbacks] = useState<NavigationShortcutCallbacks>({});
  const trackKeyboardShortcutUsage = useEventTracker();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && (event.key === '.' || event.code === 'Period') && !event.repeat) {
        if (shortcutCallbacks.nextButtonCallback) {
          shortcutCallbacks.nextButtonCallback();
          trackKeyboardShortcutUsage({
            trackingName: 'quick_grader_linear_nav_next',
            action: 'keydown',
            trackingData: {},
          });
        }
      }
      if ((event.ctrlKey || event.metaKey) && (event.key === ',' || event.code === 'Comma') && !event.repeat) {
        if (shortcutCallbacks.previousButtonCallback) {
          shortcutCallbacks.previousButtonCallback();
          trackKeyboardShortcutUsage({
            trackingName: 'quick_grader_linear_nav_previous',
            action: 'keydown',
            trackingData: {},
          });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, false);

    return () => document.removeEventListener('keydown', handleKeyDown, false);
    // We want to register the event listener only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return shortcutCallbacks;
};

// Linear Navigation controls.
const LinearNav = () => {
  const { submitterList, submissionData, getUrl, reentryLog } = useContext(SGAPageContext);
  const { hasUnsavedChanges } = useContext(AutoSaveContext);

  const shortcutCallbacks = useShortcuts();

  // Handle use case where submitter requested in URL is not found on the list of enrolled learners
  if (!submissionData.data?.submitter) {
    return null;
  }

  const { submission, isTeamAssignment } = submissionData.data;

  const { previousSubmitter, nextSubmitter, submittedGroup } = submitterList.data || {};

  let nextButtonInfo: LinearNavigationButtonInfo = null;
  let previousButtonInfo: LinearNavigationButtonInfo = null;

  // if the loading is complete and the submissionManager is established within "submission" then load next button info
  if (!submissionData.loading && !submissionData.error && submission) {
    nextButtonInfo = submission.nextButtonInfo();
    previousButtonInfo = submission.previousButtonInfo();
  }

  const autoFocusNextButton = shouldAutoFocusNextButton(
    submissionData.loading || submissionData.error !== undefined,
    submission
  );

  // Put the next button first in the code. This way when the user uses the TAB key to get from rubric to navigation,
  // they will get to the next button first.
  return (
    <Grid container spacing={16} css={styles.container}>
      <Grid item xs css={styles.buttonColumn}>
        <NextButtonSubmitterFlow
          // To trigger autofocus, we need to completely re-render the button. This will happen if the key property is
          // changed when navigating. This needs to include the `autoFocusNextButton` that will trigger autofocus
          // when the submission finishes loading.
          key={`next_${submissionData.data?.submitter?.id}_${submission?.currentQuestionId}_${submission?.currentRubricId}_${autoFocusNextButton}`}
          nextButtonInfo={nextButtonInfo}
          disabled={submissionData.loading || (!nextButtonInfo && (submitterList.loading || hasUnsavedChanges))}
          currentSubmitterId={submissionData.data.submitter.id}
          nextSubmitter={nextSubmitter || null}
          submittedGroup={submittedGroup || []}
          isTeamAssignment={isTeamAssignment}
          getUrlCallback={getUrl}
          shortcutCallbacks={shortcutCallbacks}
          autoFocus={autoFocusNextButton}
          reentryLog={reentryLog}
        />
        <div css={[styles.shortcuts, styles.alignRight]}>
          {_t('Shortcut: use')} <strong css={styles.shortcut}>Ctrl</strong>
          {' + '}
          <strong css={styles.shortcut}>
            <span css={styles.shortcutCharacter}>.</span>
          </strong>
        </div>
      </Grid>
      <Grid item xs css={styles.buttonColumn}>
        <PreviousButtonSubmitterFlow
          previousButtonInfo={previousButtonInfo}
          disabled={submissionData.loading || (!previousButtonInfo && (submitterList.loading || hasUnsavedChanges))}
          currentSubmitterId={submissionData.data.submitter.id}
          previousSubmitter={previousSubmitter || null}
          getUrlCallback={getUrl}
          shortcutCallbacks={shortcutCallbacks}
        />
        <div css={styles.shortcuts}>
          {_t('Shortcut: use')} <strong css={styles.shortcut}>Ctrl</strong>
          {' + '}
          <strong css={styles.shortcut}>
            <span css={styles.shortcutCharacter}>,</span>
          </strong>
        </div>
      </Grid>
    </Grid>
  );
};

export default LinearNav;
